import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  Form,
  Label,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Offcanvas,
  OffcanvasHeader,
  Input,
} from "reactstrap";
import Select from "react-select";
import ItemService from "../../../services/OrderingServices/ItemService";
import UnitService from "../../../services/OrderingServices/UnitService";
import RecipeService from "../../../services/OrderingServices/RecipeService";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

const ReceipeForm = ({ modal, toggleModal, fetchRecipes, selectedRecipe }) => {
  const [open, setOpen] = useState("");
  const [unitList, setUnitList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const navigate = useNavigate();

  const toggle = (id) => setOpen(open === id ? "" : id);

  const fetchRecipeDetail = async () => {
    if (!selectedRecipe) return;
    try {
      const response = await RecipeService.getDetail(selectedRecipe);
      setInitialData(response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await ItemService.itemList();
      setProductData(response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUnit = async () => {
    try {
      const response = await UnitService.getList();
      setUnitList(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchRecipeDetail();
    fetchProduct();
    fetchUnit();
  }, [selectedRecipe]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: initialData?.id || 0,
      productName: initialData?.productName || "",
      productId: initialData?.productId || 0,
      description: initialData?.description || "",
      recipeDetails: initialData?.recipeDetails || [
        {
          id: 0,
          ingredientId: 0,
          ingredientName: "",
          quantity: 0,
          unitId: 0,
          description: "",
        },
      ],
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        if (selectedRecipe) {
          await RecipeService.update(selectedRecipe, values);
          toast.success("Recipe updated successfully!");
        } else {
          await RecipeService.create(values);
          toast.success("Recipe created successfully!");
          resetForm();
        }
        fetchRecipes();
        toggleModal();
      } catch (error) {
        console.error("Submit Error:", error);
        toast.error("Failed to save recipe. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleAddIngredient = () => {
    formik.setFieldValue("recipeDetails", [
      ...formik.values.recipeDetails,
      {
        id: 0,
        ingredientId: 0,
        ingredientName: "",
        price: 0,
        quantity: 0,
        unitId: 0,
        description: "",
      },
    ]);
  };

  const handleRemoveIngredient = (index) => {
    const updated = [...formik.values.recipeDetails];
    updated.splice(index, 1);
    formik.setFieldValue("recipeDetails", updated);
  };

  const productOptions =
    productData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
      price: type.price,
      description: type.description,
    })) || [];

  const unitOptions =
    unitList?.map((type) => ({ value: type.id, label: type.name })) || [];

  const handleProductChange = (selectedOption) => {
    formik.setFieldValue(
      "productId",
      selectedOption ? selectedOption.value : 0
    );
    formik.setFieldValue(
      "productName",
      selectedOption ? selectedOption.label : ""
    );
    formik.setFieldValue("price", selectedOption ? selectedOption.price : 0);
    formik.setFieldValue(
      "description",
      selectedOption ? selectedOption.description : ""
    );
  };

  const handleIngredientChange = (selectedOption, index) => {
    formik.setFieldValue(
      `recipeDetails[${index}].ingredientId`,
      selectedOption ? selectedOption.value : 0
    );
    formik.setFieldValue(
      `recipeDetails[${index}].ingredientName`,
      selectedOption ? selectedOption.label : ""
    );
    formik.setFieldValue(
      `recipeDetails[${index}].price`,
      selectedOption ? selectedOption.price : 0
    );
    formik.setFieldValue(
      `recipeDetails[${index}].quantity`,
      selectedOption ? 1 : 0
    );
    formik.setFieldValue(
      `recipeDetails[${index}].description`,
      selectedOption ? selectedOption.description : ""
    );
  };

  const handleUnitChange = (selectedOption, index) => {
    formik.setFieldValue(
      `recipeDetails[${index}].unitId`,
      selectedOption ? selectedOption.value : 0
    );
  };

  return (
    <Offcanvas
      isOpen={modal}
      toggle={toggleModal}
      direction="end"
      backdrop={true}
      style={{ width: "600px" }}
    >
      <OffcanvasHeader toggle={toggleModal} className="border-bottom">
        <i className="ri-stack-line me-2"></i>
        {selectedRecipe ? "Update Recipe" : "Add Recipe"}
      </OffcanvasHeader>

      <Form
        onSubmit={formik.handleSubmit}
        className="d-flex flex-column"
        style={{ height: "calc(100vh - 73px)" }}
      >
        {/* SCROLLABLE CONTENT */}
        <div className="flex-grow-1 overflow-auto p-3">
          {/* Product Info */}
          <Card className="mb-3">
            <CardBody>
              <Row>
                <Col md={12} className="mb-2">
                  <Label>Product</Label>
                  <Select
                    id="productId"
                    name="productId"
                    options={productOptions}
                    value={
                      productOptions.find(
                        (option) => option.value === formik.values.productId
                      ) || null
                    }
                    onChange={handleProductChange}
                    placeholder="Select Product"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </Col>
                <Col md={12}>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows={4}
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Ingredients */}
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5>Recipe Ingredients</h5>
              </div>

              <Accordion open={open} toggle={toggle}>
                {formik.values.recipeDetails.map((item, index) => (
                  <AccordionItem key={index}>
                    <AccordionHeader targetId={index.toString()}>
                      <div className="d-flex justify-content-between w-100 align-items-center">
                        <h6>
                          {item.ingredientName || `Ingredient #${index + 1}`}
                        </h6>
                        <Button
                          color="light"
                          size="sm"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveIngredient(index);
                          }}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      </div>
                    </AccordionHeader>
                    <AccordionBody accordionId={index.toString()}>
                      <Row>
                        <Col md={4} className="mb-2">
                          <Select
                            id={`ingredientId-${index}`}
                            options={productOptions}
                            value={
                              productOptions.find(
                                (option) => option.value === item.ingredientId
                              ) || null
                            }
                            onChange={(selectedOption) =>
                              handleIngredientChange(selectedOption, index)
                            }
                            placeholder="Ingredient"
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </Col>
                        <Col md={4} className="mb-2">
                          <Input
                            name={`recipeDetails[${index}].quantity`}
                            type="number"
                            value={item.quantity}
                            onChange={formik.handleChange}
                            placeholder="Quantity"
                          />
                        </Col>
                        <Col md={4} className="mb-2">
                          <Select
                            id={`unitId-${index}`}
                            options={unitOptions}
                            value={
                              unitOptions.find(
                                (option) => option.value === item.unitId
                              ) || null
                            }
                            onChange={(selectedOption) =>
                              handleUnitChange(selectedOption, index)
                            }
                            placeholder="Unit"
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </Col>
                        <Col md={12} className="mt-2">
                          <Input
                            name={`recipeDetails[${index}].description`}
                            type="textarea"
                            rows={2}
                            value={item.description}
                            placeholder="Description"
                            onChange={formik.handleChange}
                          />
                        </Col>
                      </Row>
                    </AccordionBody>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Add Ingredient Button */}
              <div className="d-flex justify-content-center mt-2">
                <Button
                  color="success"
                  size="sm"
                  type="button"
                  onClick={handleAddIngredient}
                >
                  <i className="bx bx-plus"></i> Add Ingredient
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* FIXED FOOTER BUTTONS */}
        <div className="bg-white border-top p-3 d-flex justify-content-end gap-2">
          <Button color="danger" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="success" type="submit" disabled={loading}>
            {selectedRecipe ? "Update" : "Create"}
          </Button>
        </div>
      </Form>
    </Offcanvas>
  );
};

export default ReceipeForm;